import type { DateStringWithTime } from '@/types/common';
import type {
  SalesforceCalculatePriceRequest,
  SalesforceCalculatePriceRequestIndividual,
  SalesforceCalculatePriceResponse,
  SalesforcePlanPricingRequest,
  SalesforcePlanPricingRequestIndividual,
  SalesforcePlanPricingResponse,
} from '@/types/salesforce';
import { calculateAge } from '@/utils/pediatrics';
import type { WizardFormData } from '../types';

function buildCalculatePlanPricingRequest(
  formData: WizardFormData
): SalesforcePlanPricingRequest {
  const members = formData.members || [];

  if (members.length === 0) {
    throw new Error('At least one member is required to calculate price');
  }

  const planMembers: SalesforcePlanPricingRequestIndividual[] = members.map(
    (member, index) => {
      const hasPediatrics = member.pediatrics_enabled === true;

      return {
        id: index.toString(),
        relationship: member.isPrimary ? 'Primary' : 'Other',
        birthDate:
          `${member.date_of_birth}T00:00:00.000Z` as DateStringWithTime,
        age: calculateAge(member.date_of_birth),
        isPrimary: member.isPrimary,
        hasPediatrics,
      };
    }
  );

  return {
    planMembers,
  };
}

/**
 * Build calculate price request from wizard form data.
 *
 * @param formData - The wizard form data containing members
 * @param promoCode - Optional promo/discount code
 * @returns Calculate price request formatted for the Marketing API
 */
export function buildCalculatePriceRequest(
  formData: WizardFormData,
  promoCode?: string
): SalesforceCalculatePriceRequest {
  const members = formData.members || [];

  if (members.length === 0) {
    throw new Error('At least one member is required to calculate price');
  }

  const individuals: SalesforceCalculatePriceRequestIndividual[] = members.map(
    member => {
      const hasPediatrics = member.pediatrics_enabled === true;

      return {
        name: `${member.first_name} ${member.last_name}`,
        relationship: member.isPrimary ? 'Primary' : 'Other',
        dateOfBirth:
          `${member.date_of_birth}T00:00:00.000Z` as DateStringWithTime,
        isPrimary: member.isPrimary,
        hasPediatrics,
        plan: member.plan,
      };
    }
  );

  return {
    individuals,
    promoCode: promoCode || undefined,
  };
}

/**
 * Calculate price via API route.
 *
 * @param formData - The wizard form data containing members
 * @param promoCode - Optional promo/discount code
 * @returns The calculate price response with individual and total prices
 */
export async function calculatePrice(
  formData: WizardFormData,
  promoCode?: string
): Promise<SalesforceCalculatePriceResponse> {
  const payload = buildCalculatePriceRequest(formData, promoCode);

  const response = await fetch('/api/calculate-quote', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      `Failed to calculate price: ${error.error || response.statusText}`
    );
  }

  return response.json();
}

/**
 * Calculate plan pricing via API route.
 *
 * @param formData - The wizard form data containing members
 * @returns The calculate price response with individual and total prices
 */
export async function calculatePlanPricing(
  formData: WizardFormData
): Promise<SalesforcePlanPricingResponse[]> {
  const payload = buildCalculatePlanPricingRequest(formData);

  const response = await fetch('/api/calculate-plan-pricing', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      `Failed to calculate price: ${error.error || response.statusText}`
    );
  }

  return response.json();
}
